'use client';

import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
// import Image from "next/image";
import {
  ChevronRight,
  Send,
  Star,
  Minimize2,
  Maximize2,
  Maximize,
  Minimize,
  Subtitles,
  Calendar,
  Clock,
  User,
  // Play,
  // Pause,
  Fullscreen,
  Download,
  AlertCircle,
  GraduationCap,
  MessageCircle,
  Quote,
  Loader2,
} from "lucide-react";
import { VideoPlayer } from "@/components/VideoPlayer";
import { VideoPlayerVidstack } from "@/components/VideoPlayerVidstack";
import { ContentTabs } from "@/components/episode/ContentTabs";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { usePlayer } from "@/contexts/player-context";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { VideoDownloadDialog } from "@/components/VideoDownloadDialog";
import { useYouTubeAccessibility } from "@/hooks/useYouTubeAccessibility";
import { YouTubePlayerV3 } from "@/components/YouTubePlayerV3";
import { WordHoverCard } from "@/components/vocabulary/WordHoverCard";
import { useWordExplanation } from "@/hooks/use-word-explanation";
import { useYoudaoTranslation } from "@/hooks/use-youdao-translation";
import { useUser } from "@/lib/context/user-context";
import { useUserSettings } from "@/contexts/user-settings-context";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "@/components/ui/drawer";
import { SpeakerIcon } from "@/components/SpeakerIcon";
import { getSpeakerLang } from "@/utils/device-detection";
import { cn } from "@/lib/utils";
import { createVocabularyTermKey } from "@/lib/vocabulary-term-key";
import { useI18n } from "@/lib/i18n";
import { Skeleton } from "@/components/ui/skeleton";
import type { ActiveVocabularyTermData } from "./ImmersiveVocabularyCards";

interface VideoPlayerSectionProps {
  description: string;
  content: any | null;
  youtubeDuration: number;
  currentTime?: number;
  isPlaying: boolean;
  isLoading: boolean;
  isStarred: boolean;
  isMinimized: boolean;
  onYouTubeReady: (player: any) => void;
  onTimeUpdate: (time: number) => void;
  onDurationChange: (duration: number) => void;
  onPlayPause: (isPlaying: boolean) => void;
  onEnded: () => void;
  onStarClick: () => void;
  onAddToListClick: () => void;
  onShareClick: () => void;
  onToggleMinimize: () => void;
  formatDuration: (duration: number) => string;
  contentTabsActiveTab: string;
  onContentTabChange: (tab: string) => void;
  // Immersive mode props
  isImmersiveMode?: boolean;
  activeSubtitleText?: string;
  activeSubtitleTranslation?: string;
  activeSubtitleId?: string | null;
  activeSubtitleTimestamp?: [number, number] | null;
  onImmersiveModeChange?: (immersiveMode: boolean) => void;
  // Active vocabulary terms
  activeVocabularyTerms?: ActiveVocabularyTermData[];
  // Subtitle control in immersive mode
  showSubtitlesInImmersive?: boolean;
  showTranslationInImmersive?: boolean;
  // Mobile immersive mode callback
  onMobileImmersiveModeChange?: (isEnabled: boolean) => void;
  onShowSubtitlesInImmersiveChange?: (show: boolean) => void;
  onRestoreActiveSubtitle?: () => void;
}

const USE_VIDSTACK_PLAYER = true;
const USE_VIDSTACK_FOR_YOUTUBE = true;

type Html5PlayerApi = {
  seekTo: (time: number) => void;
  setPlaybackRate: (speed: number) => void;
  getCurrentTime: () => number;
  play: () => void;
  pause: () => void;
};

type TranscriptChunk = {
  text?: string;
  translation?: string | Record<string, string>;
  timestamp?: [number, number];
};

type TranscriptEntry = TranscriptChunk & {
  chunks?: TranscriptChunk[];
};

type VocabularyHighlightStyle = {
  textClass?: string;
  backgroundClass?: string;
};

const DEFAULT_SUBTITLE_HIGHLIGHT_STYLE: VocabularyHighlightStyle = {
  backgroundClass: "bg-[#d97757]",
  textClass: "text-white",
};

const VOCAB_SUFFIXES = ["ed", "ing", "s", "er", "est", "ly"] as const;

function normalizeToken(token: string): string {
  return token
    .replace(/^[^a-zA-Z']+|[^a-zA-Z']+$/g, "")
    .toLowerCase();
}

function matchesWithSuffix(base: string, candidate: string): boolean {
  if (!base || !candidate) {
    return false;
  }

  if (candidate === base) {
    return true;
  }

  return VOCAB_SUFFIXES.some(
    (suffix) =>
      candidate === `${base}${suffix}` ||
      (candidate.startsWith(base) && candidate.endsWith(suffix)),
  );
}

function formatVttTimestamp(timeInSeconds: number): string {
  const totalMilliseconds = Math.max(0, Math.round(timeInSeconds * 1000));
  const hours = Math.floor(totalMilliseconds / 3_600_000)
    .toString()
    .padStart(2, "0");
  const minutes = Math.floor((totalMilliseconds % 3_600_000) / 60_000)
    .toString()
    .padStart(2, "0");
  const seconds = Math.floor((totalMilliseconds % 60_000) / 1000)
    .toString()
    .padStart(2, "0");
  const milliseconds = (totalMilliseconds % 1000).toString().padStart(3, "0");
  return `${hours}:${minutes}:${seconds}.${milliseconds}`;
}

function transcriptToVtt(
  transcript: TranscriptEntry[] | null | undefined,
  targetLanguageCode: string = 'zh'
): string | null {
  if (!Array.isArray(transcript) || transcript.length === 0) {
    return null;
  }

  const lines: string[] = ["WEBVTT", ""];
  let cueIndex = 1;

  for (const entry of transcript) {
    const segments =
      Array.isArray(entry?.chunks) && entry.chunks.length > 0 ? entry.chunks : [entry];

    for (const segment of segments) {
      const start = segment?.timestamp?.[0] ?? entry?.timestamp?.[0];
      const end = segment?.timestamp?.[1] ?? entry?.timestamp?.[1];
      const originalText = (segment?.text ?? entry?.text ?? "").trim();
      
      const rawTranslation = segment?.translation ?? entry?.translation;
      let translation = "";
      
      if (typeof rawTranslation === 'string') {
        translation = rawTranslation.trim();
      } else if (typeof rawTranslation === 'object' && rawTranslation !== null) {
        translation = (rawTranslation[targetLanguageCode] || rawTranslation['zh'] || Object.values(rawTranslation)[0] || "").trim();
      }
      
      const resolvedText = translation || originalText;

      if (
        !resolvedText ||
        typeof start !== "number" ||
        typeof end !== "number" ||
        Number.isNaN(start) ||
        Number.isNaN(end) ||
        end <= start
      ) {
        continue;
      }

      lines.push(String(cueIndex++));
      lines.push(`${formatVttTimestamp(start)} --> ${formatVttTimestamp(end)}`);
      lines.push(resolvedText);
      lines.push("");
    }
  }

  if (lines.length <= 2) {
    return null;
  }

  return lines.join("\n");
}

// const SpinnerIcon = () => (
//   <div className="flex justify-center items-center w-full h-8 text-primary">
//     <div className="animate-spin rounded-full h-4 w-4 border-b-[2px] border-primary"></div>
//   </div>
// );

function VideoPlayerSectionComponent({
  description,
  content,
  youtubeDuration,
  currentTime,
  isPlaying,
  isLoading,
  isStarred,
  isMinimized,
  onYouTubeReady,
  onTimeUpdate,
  onDurationChange,
  onPlayPause,
  onEnded,
  onStarClick,
  onAddToListClick,
  onShareClick,
  onToggleMinimize,
  formatDuration,
  contentTabsActiveTab,
  onContentTabChange,
  isImmersiveMode = false,
  activeSubtitleText,
  activeSubtitleTranslation,
  activeSubtitleId,
  activeSubtitleTimestamp,
  onImmersiveModeChange,
  activeVocabularyTerms = [],
  showSubtitlesInImmersive = true,
  showTranslationInImmersive = true,
  onMobileImmersiveModeChange,
  onShowSubtitlesInImmersiveChange,
  onRestoreActiveSubtitle,
}: VideoPlayerSectionProps) {
  // 🚀 性能优化：缓存移动端检测结果
  const isMobile = useMemo(() => {
    if (typeof window === "undefined") return false;
    const { innerWidth, navigator } = window;
    const userAgent = navigator.userAgent || "";
    const platform = navigator.platform || "";
    const uaDataPlatform =
      (navigator as typeof navigator & { userAgentData?: { platform?: string } })
        ?.userAgentData?.platform || "";
    const platformSignature = `${platform} ${uaDataPlatform}`.toLowerCase();
    const maxTouchPoints = navigator.maxTouchPoints || 0;

    const mobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    const isLegacyMobile = mobileUserAgent.test(userAgent);
    const isTouchDevice = "ontouchstart" in window || maxTouchPoints > 1;
    const isPhoneSized = innerWidth <= 768;
    const isTabletSized = innerWidth <= 1366;
    const isModernIPad =
      !isLegacyMobile &&
      isTouchDevice &&
      (platformSignature.includes("mac") || platformSignature.includes("ipad"));
    const isIPadPlatform = platformSignature.includes("ipad");

    return (
      isLegacyMobile ||
      isIPadPlatform ||
      (isTouchDevice && (isPhoneSized || isTabletSized)) ||
      isModernIPad
    );
  }, []);

  const { user } = useUser();
  const { userSettings } = useUserSettings();
  const { t } = useI18n();
  
  const userTargetLanguageCode = useMemo(() => {
    return userSettings?.languageSetting?.split('-')[0]?.toLowerCase() || 'zh';
  }, [userSettings?.languageSetting]);

  const [selectedWord, setSelectedWord] = useState<string>("");
  const [showWordDrawer, setShowWordDrawer] = useState(false);
  const speakerRef = useRef<HTMLDivElement>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const originalBodyOverflowRef = useRef<string | null>(null);
  const originalBodyPaddingRightRef = useRef<string | null>(null);
  const html5PlayerApiRef = useRef<Html5PlayerApi | null>(null);
  const { seekTo: globalSeekTo, duration: globalDuration } = usePlayer();
  const [isSliderDragging, setIsSliderDragging] = useState(false);
  const [sliderValue, setSliderValue] = useState(currentTime ?? 0);
  const [showMobileFullscreenControls, setShowMobileFullscreenControls] = useState(false);
  const mobileControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const subtitlesPointerHandledRef = useRef(false);
  const highlightedSubtitleTermsRef = useRef<Map<string, string>>(new Map());

  const {
    explanations,
    loading: explanationLoading,
    savedWords,
    savingWord,
    getExplanation,
    saveCard,
    isCardSaved,
  } = useWordExplanation(userSettings, activeSubtitleText, user?.id);

  const {
    translations,
    loading: youdaoLoading,
    getTranslation,
    isInitialized,
  } = useYoudaoTranslation();

  const shouldShowTranslation = showTranslationInImmersive !== false;

  const minimizeButtonLabel = isMinimized
    ? t("episode.videoPlayer.controls.maximize")
    : t("episode.videoPlayer.controls.minimize");
  const episodeContext = useMemo(() => {
    const episodeContent = content?.content || {};
    const rawId =
      episodeContent.episodeId ||
      episodeContent.id ||
      content?.id ||
      content?.shareId ||
      episodeContent.videoId ||
      "";
    const rawTitle =
      episodeContent.title ||
      content?.title ||
      "";
    const rawTitleTranslation =
      episodeContent.titleTranslation ||
      content?.titleTranslation ||
      null;
    const rawThumbnail =
      episodeContent.thumbnail ||
      content?.thumbnail ||
      "";

    return {
      id: typeof rawId === "string" ? rawId : rawId ? String(rawId) : "",
      title: typeof rawTitle === "string" ? rawTitle : "",
      titleTranslation: rawTitleTranslation, // Keep as string or object
      thumbnail:
        typeof rawThumbnail === "string" ? rawThumbnail : "",
    };
  }, [content]);

  const episodeId = episodeContext.id;
  useEffect(() => {
    highlightedSubtitleTermsRef.current.clear();
  }, [episodeId]);
  const episodeTitle = episodeContext.title;
  const episodeThumbnail = episodeContext.thumbnail;
  const preferChineseTitles = userSettings?.preferChineseTitles;

  // State for dynamically generated title translation
  const [generatedTitleTranslation, setGeneratedTitleTranslation] = useState<string | Record<string, string> | null>(null);
  const titleTranslationGenerationAttemptedRef = useRef(false);

  // Get title translation for user's language
  const episodeTitleTranslation = useMemo((): string => {
    const rawTranslation = generatedTitleTranslation || episodeContext.titleTranslation;
    
    if (!rawTranslation) return "";
    
    // If it's a string, return as-is (legacy format, assumed Chinese)
    if (typeof rawTranslation === "string") {
      return rawTranslation;
    }
    
    // If it's an object, get the translation for user's target language
    if (typeof rawTranslation === "object" && rawTranslation !== null) {
      const translationObj = rawTranslation as Record<string, unknown>;
      
      // Try user's language first
      const userLangTranslation = translationObj[userTargetLanguageCode];
      if (userLangTranslation && typeof userLangTranslation === "string" && userLangTranslation.trim().length > 0) {
        return userLangTranslation;
      }
      
      // Fallback to Chinese if available
      const zhTranslation = translationObj["zh"];
      if (zhTranslation && typeof zhTranslation === "string" && zhTranslation.trim().length > 0) {
        return zhTranslation;
      }
      
      // Return first available string translation
      for (const value of Object.values(translationObj)) {
        if (typeof value === "string" && value.trim().length > 0) {
          return value;
        }
      }
    }
    
    return "";
  }, [episodeContext.titleTranslation, generatedTitleTranslation, userTargetLanguageCode]);

  // Check if title translation exists for user's language
  const hasTitleTranslationForUserLanguage = useMemo((): boolean => {
    const rawTranslation = generatedTitleTranslation || episodeContext.titleTranslation;
    
    if (!rawTranslation) return false;
    
    // If it's a string (legacy format), assume it's Chinese
    if (typeof rawTranslation === "string") {
      return userTargetLanguageCode === "zh";
    }
    
    // If it's an object, check if user's language exists
    if (typeof rawTranslation === "object" && rawTranslation !== null) {
      const translationObj = rawTranslation as Record<string, unknown>;
      const translation = translationObj[userTargetLanguageCode];
      return Boolean(translation && typeof translation === "string" && translation.trim().length > 0);
    }
    
    return false;
  }, [episodeContext.titleTranslation, generatedTitleTranslation, userTargetLanguageCode]);

  // Generate title translation if missing
  useEffect(() => {
    const generateTitleTranslation = async () => {
      // Skip if already attempted, no episode ID, or user language is Chinese (we likely have Chinese)
      if (
        titleTranslationGenerationAttemptedRef.current ||
        !episodeId ||
        !episodeTitle ||
        userTargetLanguageCode === "zh" ||
        hasTitleTranslationForUserLanguage ||
        !user
      ) {
        return;
      }

      titleTranslationGenerationAttemptedRef.current = true;

      try {
        // Get session for authentication
        const { supabase } = await import('@/core/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.access_token) {
          console.warn('[VideoPlayerSection] No session available for title translation');
          titleTranslationGenerationAttemptedRef.current = false;
          return;
        }

        const response = await fetch(`/api/episodes/${episodeId}/title-translation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            targetLanguage: userSettings?.languageSetting,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.titleTranslation) {
            setGeneratedTitleTranslation(data.titleTranslation);
            console.log('[VideoPlayerSection] Title translation generated:', data.titleTranslation);
          }
        }
      } catch (error) {
        console.error('[VideoPlayerSection] Failed to generate title translation:', error);
        // Reset to allow retry
        titleTranslationGenerationAttemptedRef.current = false;
      }
    };

    // Small delay to avoid calling immediately on mount
    const timer = setTimeout(generateTitleTranslation, 500);
    return () => clearTimeout(timer);
  }, [episodeId, episodeTitle, userTargetLanguageCode, hasTitleTranslationForUserLanguage, user, userSettings?.languageSetting]);

  // Reset generation state when episode changes
  useEffect(() => {
    titleTranslationGenerationAttemptedRef.current = false;
    setGeneratedTitleTranslation(null);
  }, [episodeId]);

  const primaryDisplayTitle = useMemo(() => {
    if (preferChineseTitles && episodeTitleTranslation) {
      return episodeTitleTranslation;
    }
    return episodeTitle;
  }, [preferChineseTitles, episodeTitle, episodeTitleTranslation]);

  const transcriptData = useMemo<TranscriptEntry[] | null>(() => {
    const rawTranscript =
      content?.content?.content?.transcript ??
      content?.content?.transcript ??
      content?.transcript ??
      null;
    return Array.isArray(rawTranscript) ? (rawTranscript as TranscriptEntry[]) : null;
  }, [content]);

  const captionTrackId = useMemo(
    () => `episode-transcript-${episodeId || "unknown"}`,
    [episodeId],
  );

  const captionBlobUrlRef = useRef<string | null>(null);
  const [captionTrackSrc, setCaptionTrackSrc] = useState<string | null>(null);

  const captionTrackLabel = t(
    "episode.videoPlayer.controls.captionTrackLabel",
    "English & Chinese",
  );

  useEffect(() => {
    return () => {
      if (captionBlobUrlRef.current) {
        URL.revokeObjectURL(captionBlobUrlRef.current);
        captionBlobUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (captionBlobUrlRef.current) {
      URL.revokeObjectURL(captionBlobUrlRef.current);
      captionBlobUrlRef.current = null;
    }
    setCaptionTrackSrc(null);
  }, [captionTrackId]);

  const ensureCaptionTrack = useCallback(() => {
    if (captionTrackSrc) {
      return captionTrackSrc;
    }

    let src: string | null = null;

    if (transcriptData?.length) {
      const vtt = transcriptToVtt(transcriptData, userTargetLanguageCode);
      if (vtt) {
        try {
          const blob = new Blob([vtt], { type: "text/vtt" });
          if (captionBlobUrlRef.current) {
            URL.revokeObjectURL(captionBlobUrlRef.current);
          }
          src = URL.createObjectURL(blob);
          captionBlobUrlRef.current = src;
        } catch (error) {
          console.error("Failed to create captions blob", error);
        }
      } else {
        console.warn("Failed to build VTT from transcript data.");
      }
    }

    if (!src) {
      console.warn("No captions available for this transcript.");
      return null;
    }

    setCaptionTrackSrc(src);
    return src;
  }, [captionTrackSrc, transcriptData, userTargetLanguageCode]);

  useEffect(() => {
    if (!transcriptData?.length) {
      if (captionTrackSrc) {
        setCaptionTrackSrc(null);
      }
      return;
    }

    if (!captionTrackSrc) {
      ensureCaptionTrack();
    }
  }, [captionTrackSrc, ensureCaptionTrack, transcriptData]);

  const captionTextTracks = useMemo(() => {
    if (!captionTrackSrc) {
      return undefined;
    }

    return [
      {
        id: captionTrackId,
        src: captionTrackSrc,
        kind: "subtitles" as const,
        srclang: "zh",
        label: captionTrackLabel,
        default: false,
      },
    ];
  }, [captionTrackId, captionTrackLabel, captionTrackSrc]);

  const secondaryDisplayTitle = useMemo(() => {
    if (preferChineseTitles) {
      if (
        episodeTitle &&
        episodeTitleTranslation &&
        episodeTitle !== episodeTitleTranslation
      ) {
        return episodeTitle;
      }
      return "";
    }

    if (
      !preferChineseTitles &&
      episodeTitleTranslation &&
      episodeTitleTranslation !== episodeTitle
    ) {
      return episodeTitleTranslation;
    }

    return "";
  }, [preferChineseTitles, episodeTitle, episodeTitleTranslation]);

  const renderSubtitleWithHoverCards = useCallback(
    (text?: string) => {
      if (!text) {
        return null;
      }

      const tokenRegex =
        /\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b|\d+%?|\s+|[^\w\s]/g;
      const tokens: Array<{ value: string; start: number; end: number }> = [];
      let tokenMatch: RegExpExecArray | null;

      while ((tokenMatch = tokenRegex.exec(text)) !== null) {
        tokens.push({
          value: tokenMatch[0],
          start: tokenMatch.index,
          end: tokenMatch.index + tokenMatch[0].length,
        });
      }

      const wordTokenMeta = tokens
        .map((token, index) => {
          const normalized = normalizeToken(token.value);
          if (!normalized) {
            return null;
          }
          return { tokenIndex: index, normalized };
        })
        .filter(
          (
            item,
          ): item is { tokenIndex: number; normalized: string } =>
            Boolean(item?.normalized),
        );

      const highlightByTokenIndex = new Map<number, VocabularyHighlightStyle>();

      if (Array.isArray(activeVocabularyTerms) && activeVocabularyTerms.length && wordTokenMeta.length) {
        const currentSubtitleKey = activeSubtitleId
          ? `id:${activeSubtitleId}`
          : `text:${text}`;
        const renderedInCurrentSubtitle = new Set<string>();
        const globalHighlightStore = highlightedSubtitleTermsRef.current;

        const processed = activeVocabularyTerms
          .map((entry) => {
            const term = entry?.term?.trim();
            if (!term) {
              return null;
            }

            const normalizedTokens = term
              .split(/\s+/)
              .map((token) => normalizeToken(token))
              .filter(Boolean);

            if (normalizedTokens.length === 0) {
              return null;
            }

            const style: VocabularyHighlightStyle = {
              textClass:
                entry.textClass || DEFAULT_SUBTITLE_HIGHLIGHT_STYLE.textClass,
              backgroundClass:
                entry.lightBackgroundClass ||
                entry.backgroundClass ||
                DEFAULT_SUBTITLE_HIGHLIGHT_STYLE.backgroundClass,
            };

            return {
              tokens: normalizedTokens,
              style,
              termKey: createVocabularyTermKey(entry.term, entry.scenarioName),
            };
          })
          .filter(
            (
              item,
            ): item is {
              tokens: string[];
              style: VocabularyHighlightStyle;
              termKey: string | null;
            } => Boolean(item),
          );

        processed.forEach(({ tokens: termTokens, style, termKey }) => {
          for (
            let startIndex = 0;
            startIndex <= wordTokenMeta.length - termTokens.length;
            startIndex += 1
          ) {
            let match = true;

            for (let offset = 0; offset < termTokens.length; offset += 1) {
              const meta = wordTokenMeta[startIndex + offset];
              if (
                !meta ||
                !matchesWithSuffix(termTokens[offset]!, meta.normalized)
              ) {
                match = false;
                break;
              }
            }

            if (match) {
              const storedKey = termKey
                ? globalHighlightStore.get(termKey)
                : undefined;
              const alreadyRendered = termKey
                ? renderedInCurrentSubtitle.has(termKey)
                : false;
              const shouldHighlight =
                !termKey ||
                ((!storedKey || storedKey === currentSubtitleKey) &&
                  !alreadyRendered);

              if (!shouldHighlight) {
                continue;
              }

              if (termKey && !storedKey) {
                globalHighlightStore.set(termKey, currentSubtitleKey);
              }
              if (termKey) {
                renderedInCurrentSubtitle.add(termKey);
              }

              for (let offset = 0; offset < termTokens.length; offset += 1) {
                const meta = wordTokenMeta[startIndex + offset];
                if (meta && !highlightByTokenIndex.has(meta.tokenIndex)) {
                  highlightByTokenIndex.set(meta.tokenIndex, style);
                }
              }

              break;
            }
          }
        });
      }

      return tokens.map((token, index) => {
        const isWord = /^[a-zA-Z]+(?:'[a-zA-Z]+)?$/.test(token.value);

        if (!isWord) {
          return <span key={`subtitle-token-${index}`}>{token.value}</span>;
        }

        return (
          <WordHoverCard
            key={`subtitle-word-${index}-${token.start}`}
            word={token.value}
            index={`subtitle-${index}`}
            isSearchMatch={false}
            isWordCheckerReady={false}
            userSettings={userSettings}
            explanations={explanations}
            loading={explanationLoading}
            savedWords={savedWords}
            savingWord={savingWord}
            onGetExplanation={getExplanation}
            onSaveCard={saveCard}
            isCardSaved={isCardSaved}
            selectedWord={selectedWord}
            showWordDrawer={showWordDrawer}
            setSelectedWord={setSelectedWord}
            setShowWordDrawer={setShowWordDrawer}
            speakerRef={speakerRef}
            youdaoTranslations={translations}
            youdaoLoading={youdaoLoading}
            youdaoGetTranslation={getTranslation}
            youdaoIsInitialized={isInitialized}
            episodeId={episodeId}
            episodeTitle={primaryDisplayTitle}
            episodeThumbnail={episodeThumbnail}
            contextText={text}
            variant="subtitle"
            isHighlighted={highlightByTokenIndex.has(index)}
            highlightStyle={highlightByTokenIndex.get(index)}
            enableMobileDrawer={false}
          />
        );
      });
    },
    [
      activeVocabularyTerms,
      explanations,
      explanationLoading,
      getExplanation,
      saveCard,
      isCardSaved,
      savedWords,
      savingWord,
      selectedWord,
      showWordDrawer,
      speakerRef,
      translations,
      youdaoLoading,
      getTranslation,
      isInitialized,
      episodeId,
      primaryDisplayTitle,
      episodeThumbnail,
      userSettings,
      activeSubtitleId,
    ]
  );

  // Add state for mobile immersive mode
  const [mobileImmersiveMode, setMobileImmersiveMode] = useState(false);
  const [fullscreenMode, setFullscreenMode] = useState(false);

  const immersiveModeActive = isMobile ? mobileImmersiveMode : isImmersiveMode;
  const ImmersiveToggleIcon = (immersiveModeActive ? Minimize2 : Fullscreen);
  const immersiveButtonLabel = useMemo(() => {
    if (isMobile) {
      return immersiveModeActive
        ? t('episode.videoPlayer.immersive.exitFocus', 'Exit focus')
        : t('episode.videoPlayer.immersive.enterFocus', 'Focus mode');
    }
    return immersiveModeActive
      ? t('episode.videoPlayer.immersive.exitImmersive', 'Exit immersive')
      : t('episode.videoPlayer.immersive.enterImmersive', 'Immersive focus');
  }, [immersiveModeActive, isMobile, t]);

  const immersiveButtonTitle = useMemo(() => {
    if (isMobile) {
      return immersiveModeActive
        ? t('episode.videoPlayer.immersive.exitFocusTitle', 'Exit focus mode')
        : t('episode.videoPlayer.immersive.enterFocusTitle', 'Enter focus mode');
    }
    return immersiveModeActive
      ? t('episode.videoPlayer.immersive.exitImmersiveTitle', 'Exit immersive mode (Esc)')
      : t('episode.videoPlayer.immersive.enterImmersiveTitle', 'Enter immersive mode');
  }, [immersiveModeActive, isMobile, t]);
  const immersiveButtonWrapperClasses = cn(
    "absolute z-30 pointer-events-auto flex gap-2",
    isMobile
      ? fullscreenMode
        ? "top-3 right-3"
        : mobileImmersiveMode
          ? "bottom-3 right-3"
          : "top-3 left-3"
      : "bottom-5 right-5",
    isMobile
      ? "opacity-100 bg-transparent"
      : "transition-opacity duration-200",
    !isMobile &&
      (immersiveModeActive || fullscreenMode
        ? "opacity-100"
        : "opacity-0 group-hover:opacity-100")
  );
  const immersiveButtonClasses = cn(
    "group/immersive-toggle flex items-center rounded-full border px-4 h-10 text-sm font-medium backdrop-blur-md transition-all duration-200 shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent dark:text-white",
    immersiveModeActive
      ? "bg-primary/90 text-white hover:bg-primary border-primary/50 focus-visible:ring-primary/40"
      : "bg-white/80 text-foreground hover:bg-white border-white/60 focus-visible:ring-white/40 dark:bg-white/20 dark:text-white/90 dark:hover:bg-white/30 dark:border-white/40 dark:focus-visible:ring-white/30",
    isMobile
      ? "h-10 w-10 px-0 text-background/80 dark:text-white justify-center border-white/25 bg-transparent shadow-none backdrop-blur-none"
      : ""
  );
  const showEscapeHint = !isMobile && isImmersiveMode;
  const isFullscreenOverlayActive = fullscreenMode;
  const FullscreenToggleIcon = fullscreenMode ? Minimize : Maximize;
  const isDesktopFullscreen = fullscreenMode && !isMobile;

  const fullscreenButtonLabel = useMemo(() => {
    return fullscreenMode
      ? t('episode.videoPlayer.controls.exitFullscreen', 'Exit full screen')
      : t('episode.videoPlayer.controls.enterFullscreen', 'Full screen');
  }, [fullscreenMode, t]);
  const fullscreenButtonTitle = fullscreenButtonLabel;
  const fullscreenButtonClasses = cn(
    "group/fullscreen-toggle flex items-center rounded-full border px-4 h-10 text-sm font-medium backdrop-blur-md transition-all duration-200 shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent dark:text-white",
    fullscreenMode
      ? "bg-primary/90 text-white hover:bg-primary border-primary/50 focus-visible:ring-primary/40"
      : "bg-white/80 text-foreground hover:bg-white border-white/60 focus-visible:ring-white/40 dark:bg-white/20 dark:text-white/90 dark:hover:bg-white/30 dark:border-white/40 dark:focus-visible:ring-white/30",
    isMobile
      ? "h-10 w-10 px-0 text-background/80 dark:text-white justify-center border-white/25 bg-transparent shadow-none backdrop-blur-none"
      : ""
  );
  const subtitlesVisible = showSubtitlesInImmersive !== false;
  useEffect(() => {
    if (!isSliderDragging) {
      setSliderValue(currentTime ?? 0);
    }
  }, [currentTime, isSliderDragging]);


  const effectiveDuration = useMemo(() => {
    if (globalDuration && globalDuration > 0) {
      return globalDuration;
    }
    if (youtubeDuration && youtubeDuration > 0) {
      return youtubeDuration;
    }
    return 0;
  }, [globalDuration, youtubeDuration]);

  const sliderMax = useMemo(() => {
    if (effectiveDuration > 0) {
      return effectiveDuration;
    }
    return sliderValue > 0 ? sliderValue : 1;
  }, [effectiveDuration, sliderValue]);

  const commitSliderValue = useCallback((value: number) => {
    const clamped = Math.max(0, Math.min(value, sliderMax));
    try {
      if (globalSeekTo) {
        globalSeekTo(clamped);
      } else if (html5PlayerApiRef.current?.seekTo) {
        html5PlayerApiRef.current.seekTo(clamped);
      }
    } catch (error) {
      console.error("Error seeking video:", error);
    }
  }, [globalSeekTo, sliderMax]);

  const clearMobileControlsTimeout = useCallback(() => {
    if (mobileControlsTimeoutRef.current) {
      clearTimeout(mobileControlsTimeoutRef.current);
      mobileControlsTimeoutRef.current = null;
    }
  }, []);

  const showMobileControls = useCallback((autoHide: boolean) => {
    if (!isMobile || !fullscreenMode) {
      return;
    }
    clearMobileControlsTimeout();
    setShowMobileFullscreenControls(true);
    if (autoHide) {
      mobileControlsTimeoutRef.current = setTimeout(() => {
        setShowMobileFullscreenControls(false);
      }, 2500);
    }
  }, [clearMobileControlsTimeout, fullscreenMode, isMobile]);

  const hideMobileControls = useCallback(() => {
    clearMobileControlsTimeout();
    setShowMobileFullscreenControls(false);
  }, [clearMobileControlsTimeout]);

  const handleMobileSubtitlesToggle = useCallback(() => {
    const next = !subtitlesVisible;
    onShowSubtitlesInImmersiveChange?.(next);
    showMobileControls(true);
    if (next) {
      if (!(activeSubtitleText?.trim() || activeSubtitleTranslation?.trim())) {
        onRestoreActiveSubtitle?.();
      }
    }
  }, [
    subtitlesVisible,
    onShowSubtitlesInImmersiveChange,
    showMobileControls,
    activeSubtitleText,
    activeSubtitleTranslation,
    onRestoreActiveSubtitle
  ]);

  const handleMobileSubtitlesPointerDown = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    subtitlesPointerHandledRef.current = true;
    event.preventDefault();
    event.stopPropagation();
    handleMobileSubtitlesToggle();
  }, [handleMobileSubtitlesToggle]);

  const handleMobileSubtitlesClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    if (subtitlesPointerHandledRef.current) {
      subtitlesPointerHandledRef.current = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    handleMobileSubtitlesToggle();
  }, [handleMobileSubtitlesToggle]);

  const handleSliderChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    if (Number.isFinite(value)) {
      setSliderValue(value);
      if (!isSliderDragging) {
        commitSliderValue(value);
      }
      showMobileControls(false);
    }
  }, [commitSliderValue, isSliderDragging, showMobileControls]);

  const handleSliderPointerDown = useCallback(() => {
    setIsSliderDragging(true);
    showMobileControls(false);
  }, [showMobileControls]);

  const handleSliderPointerUp = useCallback((event: React.SyntheticEvent<HTMLInputElement>) => {
    const value = Number((event.target as HTMLInputElement).value);
    if (Number.isFinite(value)) {
      commitSliderValue(value);
    }
    setIsSliderDragging(false);
    showMobileControls(true);
  }, [commitSliderValue, showMobileControls]);

  const formattedCurrentTime = useMemo(() => {
    if (!formatDuration) {
      return "";
    }
    const cap = effectiveDuration > 0 ? effectiveDuration : sliderValue;
    const safeValue = Math.max(0, Math.min(sliderValue, cap));
    return formatDuration(safeValue);
  }, [formatDuration, sliderValue, effectiveDuration]);

  const formattedTotalTime = useMemo(() => {
    if (!formatDuration) {
      return "";
    }
    const safeDuration = effectiveDuration > 0 ? effectiveDuration : sliderMax;
    return formatDuration(safeDuration);
  }, [formatDuration, effectiveDuration, sliderMax]);
  const mobileSubtitlesButtonClasses = cn(
    "group/subtitles-toggle flex items-center justify-center rounded-full border h-10 w-10 px-0 text-sm font-medium transition-all duration-200 shadow-none backdrop-blur-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent border-white/25",
    subtitlesVisible
      ? "bg-white/90 text-background/90 hover:bg-white border-white/70 focus-visible:ring-white/50"
      : "bg-white/10 text-white/80 hover:bg-white/20 focus-visible:ring-white/40"
  );
  const toggleSubtitlesLabel = useMemo(
    () =>
      subtitlesVisible
        ? t('episode.videoPlayer.immersive.hideSubtitles', 'Hide subtitles')
        : t('episode.videoPlayer.immersive.showSubtitles', 'Show subtitles'),
    [subtitlesVisible, t]
  );

  const handleHtml5PlayerReady = useCallback((api: Html5PlayerApi) => {
    html5PlayerApiRef.current = api;
    onYouTubeReady(api);
  }, [onYouTubeReady]);

  useEffect(() => {
    if (!(isMobile && fullscreenMode)) {
      hideMobileControls();
      clearMobileControlsTimeout();
      return;
    }

    showMobileControls(true);

    return () => {
      hideMobileControls();
      clearMobileControlsTimeout();
    };
  }, [clearMobileControlsTimeout, hideMobileControls, isMobile, fullscreenMode, showMobileControls]);

  // Check if this is a YouTube video
  const isYouTubeVideo = useMemo(() => {
    const videoUrl = content?.content?.videoUrl;
    
    // 如果是 blob storage URL，不是 YouTube 视频
    if (videoUrl && videoUrl.includes('blob.vercel-storage.com')) {
      return false;
    }
    // 只有 URL 实际包含 YouTube 域名的才是 YouTube 视频
    return videoUrl && (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be'));
  }, [content]);

  // Check if this is a podcast
  const isPodcast = useMemo(() => {
    return content?.content.origin === 'podcast';
  }, [content]);


  // YouTube accessibility and download states
  const { isAccessible: isYouTubeAccessible, isChecking } = useYouTubeAccessibility();
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [localVideoUrl, setLocalVideoUrl] = useState<string | null>(null);

  // Check for existing downloaded video on mount
  useEffect(() => {
    const checkExistingDownload = async () => {
      if (isYouTubeVideo && content?.content?.videoId) {
        try {
          // Check if Redis has updated data with downloaded video
          const response = await fetch(`/api/episodes/${content.content.videoId}`, {
            cache: 'no-cache'
          });
          
          if (response.ok) {
            const episodeData = await response.json();
            
            // Check if the episode data contains a downloaded video URL
            const downloadedUrl = episodeData.content?.content?.downloadedVideoUrl;
            const videoUrl = episodeData.content?.content?.videoUrl;
            
            if (downloadedUrl && downloadedUrl.includes('blob.vercel-storage.com')) {
              console.log('🎬 Found existing downloaded video in Redis:', downloadedUrl);
              setLocalVideoUrl(downloadedUrl);
            } else if (videoUrl && videoUrl.includes('blob.vercel-storage.com')) {
              console.log('🎬 Found blob video URL in Redis:', videoUrl);
              setLocalVideoUrl(videoUrl);
            }
          }
        } catch (error) {
          console.error('Error checking for existing download:', error);
        }
      }
    };

    checkExistingDownload();
  }, [isYouTubeVideo, content?.content?.videoId]);

  // Show download dialog when YouTube is not accessible for YouTube videos
  useEffect(() => {
    if (isYouTubeVideo && !isYouTubeAccessible && !isChecking && !localVideoUrl) {
      const timer = setTimeout(() => {
        setShowDownloadDialog(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [isYouTubeVideo, isYouTubeAccessible, isChecking, localVideoUrl]);

  const handleImmersiveModeToggle = useCallback(async () => {
    if (isMobile) {
      const newState = !mobileImmersiveMode;
      setMobileImmersiveMode(newState);
      onMobileImmersiveModeChange?.(newState);
      return;
    }

    onImmersiveModeChange?.(!isImmersiveMode);
  }, [isMobile, mobileImmersiveMode, onMobileImmersiveModeChange, onImmersiveModeChange, isImmersiveMode]);

  const canShowMinimizeInPlayer =
    !isMinimized && !isImmersiveMode && !mobileImmersiveMode && !fullscreenMode;

  const immersiveLayoutSlot = useMemo<React.ReactNode>(() => {
    if (!USE_VIDSTACK_PLAYER) {
      return null;
    }

    return (
      <div className="flex items-center gap-2">
        {canShowMinimizeInPlayer ? (
          <button
            type="button"
            className={cn(
              "vds-button flex items-center justify-center rounded-lg border border-white/20 h-9 w-9 text-white/90 transition-all duration-200 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
            )}
            aria-label={minimizeButtonLabel}
            title={minimizeButtonLabel}
            onClick={(event) => {
              event.stopPropagation();
              onToggleMinimize();
            }}
          >
            <Minimize2 className="h-5 w-5" strokeWidth={2.8} />
          </button>
        ) : null}
        <button
          type="button"
          className={cn(
            "vds-button flex items-center justify-center rounded-lg border border-white/20 h-9 w-9 text-white/90 transition-all duration-200 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
          )}
          style={{ transform: "translateX(-2px)" }}
          aria-label={immersiveButtonTitle}
          title={immersiveButtonTitle}
          aria-pressed={immersiveModeActive}
          data-active={immersiveModeActive ? "" : undefined}
          onClick={(event) => {
            event.stopPropagation();
            handleImmersiveModeToggle();
          }}
        >
          <ImmersiveToggleIcon className="h-5 w-5" strokeWidth={2.8} />
        </button>
      </div>
    );
  }, [
    canShowMinimizeInPlayer,
    handleImmersiveModeToggle,
    minimizeButtonLabel,
    immersiveButtonTitle,
    immersiveModeActive,
    onToggleMinimize,
  ]);

  const handleFullscreenToggle = useCallback(() => {
    setFullscreenMode((prev) => !prev);
  }, []);

  const resolvedMediaUrl = useMemo(() => {
    const origin = content?.content?.origin;
    const videoUrl = content?.content?.videoUrl;
    const audioUrl = content?.content?.audioUrl;
    
    // 智能选择媒体 URL：
    // 1. 如果有 localVideoUrl（已下载的视频），优先使用
    // 2. 如果 videoUrl 是真正的视频文件（.mp4, .webm, .mov 等），优先使用视频
    // 3. 根据 origin 类型选择
    // 4. 回退到任何可用的 URL
    
    let preferredFromContent: string | undefined;
    
    // 检查 videoUrl 是否是真正的视频文件
    const isVideoFile = videoUrl && /\.(mp4|webm|mov|m4v|mkv)(\?|$)/i.test(videoUrl);
    
    if (isVideoFile) {
      // videoUrl 是视频文件，优先使用
      preferredFromContent = videoUrl;
    } else if (origin === 'video') {
      preferredFromContent = videoUrl || audioUrl;
    } else if (origin === 'podcast') {
      preferredFromContent = audioUrl || videoUrl;
    } else {
      // origin 未定义时，优先使用视频（如果存在）
      preferredFromContent = videoUrl || audioUrl;
    }
    
    const finalUrl = localVideoUrl || preferredFromContent || '';
    
    return finalUrl;
  }, [content, localVideoUrl]);

  const mediaArtwork = useMemo(() => {
    const thumbnail = content?.content?.thumbnail;
    if (!thumbnail) {
      return undefined;
    }
    return [
      { src: thumbnail, sizes: '96x96', type: 'image/jpeg' },
      { src: thumbnail, sizes: '128x128', type: 'image/jpeg' },
      { src: thumbnail, sizes: '192x192', type: 'image/jpeg' },
      { src: thumbnail, sizes: '256x256', type: 'image/jpeg' },
      { src: thumbnail, sizes: '384x384', type: 'image/jpeg' },
      { src: thumbnail, sizes: '512x512', type: 'image/jpeg' },
    ] as Array<{ src: string; sizes: string; type: string }>;
  }, [content?.content?.thumbnail]);

  const showImmersiveOverlay = !USE_VIDSTACK_PLAYER;


  useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return;
    }

    const body = document.body;
    const dispatchChange = (active: boolean) => {
      try {
        window.dispatchEvent(
          new CustomEvent("nf-video-fullscreen-change", {
            detail: { active },
          })
        );
      } catch {
        // ignore dispatch errors
      }
    };

    if (!isFullscreenOverlayActive) {
      if (originalBodyOverflowRef.current !== null) {
        body.style.overflow = originalBodyOverflowRef.current;
        body.style.paddingRight = originalBodyPaddingRightRef.current ?? "";
        originalBodyOverflowRef.current = null;
        originalBodyPaddingRightRef.current = null;
      }
      body.classList.remove("nf-video-fullscreen");
      dispatchChange(false);
      return;
    }

    if (originalBodyOverflowRef.current === null) {
      originalBodyOverflowRef.current = body.style.overflow;
      originalBodyPaddingRightRef.current = body.style.paddingRight;
    }

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }
    body.classList.add("nf-video-fullscreen");
    dispatchChange(true);

    return () => {
      if (originalBodyOverflowRef.current !== null) {
        body.style.overflow = originalBodyOverflowRef.current;
        body.style.paddingRight = originalBodyPaddingRightRef.current ?? "";
        originalBodyOverflowRef.current = null;
        originalBodyPaddingRightRef.current = null;
      }
      body.classList.remove("nf-video-fullscreen");
      dispatchChange(false);
    };
  }, [isFullscreenOverlayActive]);

  useEffect(() => {
    if (isMinimized && fullscreenMode) {
      setFullscreenMode(false);
    }
  }, [isMinimized, fullscreenMode]);

  const shouldShowDesktopFullscreenSubtitles =
    fullscreenMode &&
    !isMobile &&
    subtitlesVisible &&
    (activeSubtitleText?.trim() || activeSubtitleTranslation?.trim());

  const shouldUseVidstackForYouTube =
    USE_VIDSTACK_PLAYER && USE_VIDSTACK_FOR_YOUTUBE && isYouTubeVideo;

  const shouldUseYouTubeIframePlayer =
    isYouTubeVideo && !shouldUseVidstackForYouTube;

  const shouldShowMobileFullscreenControls = isMobile && fullscreenMode && (showMobileFullscreenControls || isSliderDragging);

  const shouldHideForMinimized =
    isMinimized && !fullscreenMode && !mobileImmersiveMode;
  const shouldShowSkeleton = isLoading || !content;

  if (shouldShowSkeleton) {
    return (
      <div
        className={cn(
          "z-10 transition-all duration-300 ease-in-out",
          fullscreenMode
            ? isMobile
              ? "fixed inset-0 w-screen h-screen bg-black text-white pointer-events-auto"
              : "fixed inset-0 w-screen h-screen bg-black text-white pointer-events-auto flex items-center justify-center"
            : "sticky top-0 sm:top-[40px] bg-background/95 backdrop-blur-sm border-border/50"
        )}
      >
        <div
          className={cn(
            "max-w-7xl mx-auto w-full px-2 sm:px-0 py-3 sm:py-4",
            shouldHideForMinimized ? "hidden" : "block"
          )}
        >
          <div className="space-y-5 sm:space-y-6">
            <div className="flex flex-col items-start space-y-4 lg:flex-row lg:space-x-8 lg:space-y-0">
              <div className="w-full lg:w-[420px] lg:flex-shrink-0">
                <div className="rounded-3xl border border-border/50 bg-background/60 overflow-hidden">
                  <Skeleton className="w-full aspect-video" />
                </div>
              </div>
              <div className="hidden flex-1 flex-col space-y-4 lg:flex">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="space-y-3">
                  <Skeleton className="h-8 w-3/4" />
                  <Skeleton className="h-6 w-1/2" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-4/5" />
                  </div>
                </div>
                <div className="rounded-xl border border-border/50 bg-muted/30 p-4">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-1/3" />
                      <Skeleton className="h-3 w-1/4" />
                    </div>
                    <Skeleton className="h-4 w-4 rounded-full" />
                  </div>
                </div>
              </div>
            </div>
            {/* <div className="space-y-3 lg:hidden">
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div> */}
            {!mobileImmersiveMode && !fullscreenMode && (
              <div className="rounded-2xl border border-border/40 bg-background/60 p-2">
                <div className="flex flex-wrap gap-3">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton key={index} className="h-9 w-24 rounded-full" />
                  ))}
                </div>
                {/* <Skeleton className="mt-4 h-24 w-full rounded-2xl" /> */}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (fullscreenMode && !isMobile) {
    return (
      <>
        <div className="fixed inset-0 z-[60] flex flex-col bg-black text-white">
          {showImmersiveOverlay && (
            <div className="absolute top-4 right-4 z-[65] flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleImmersiveModeToggle}
                className={immersiveButtonClasses}
                title={immersiveButtonTitle}
                aria-label={immersiveButtonTitle}
                aria-pressed={immersiveModeActive}
              >
                <ImmersiveToggleIcon className="h-4 w-4" />
                <span>{immersiveButtonLabel}</span>
              </Button>
            </div>
          )}

          <div className="flex-1 relative flex items-center justify-center">
            <div className="relative flex items-center justify-center max-h-full max-w-full">
              {isYouTubeVideo && content?.content.videoId && isYouTubeAccessible && !localVideoUrl ? (
                <YouTubePlayerV3
                  key="youtube-player-v3"
                  videoId={content.content.videoId}
                  className="w-full h-full"
                  currentTime={currentTime}
                  onReady={onYouTubeReady}
                  onTimeUpdate={onTimeUpdate}
                  onDurationChange={onDurationChange}
                  isPlaying={isPlaying}
                  onPlayPause={onPlayPause}
                  onEnded={onEnded}
                  title={primaryDisplayTitle}
                  muted={false}
                />
              ) : (
                USE_VIDSTACK_PLAYER ? (
                  <VideoPlayerVidstack
                    key="main-video-player-desktop-fullscreen"
                    url={resolvedMediaUrl}
                    thumbnailUrl={content?.content.thumbnail}
                    className="max-h-full max-w-full"
                    currentTime={currentTime}
                    onSeekTo={handleHtml5PlayerReady}
                    onTimeUpdate={onTimeUpdate}
                    onDurationChange={onDurationChange}
                    isPlaying={isPlaying}
                    onPlayPause={onPlayPause}
                    onEnded={onEnded}
                    title={primaryDisplayTitle}
                    artist={content?.content.author}
                    album="Podcast Episode"
                    muted={false}
                    enableTapToggle
                    maintainAspectRatio={false}
                    artwork={mediaArtwork}
                    immersiveSlot={immersiveLayoutSlot}
                    textTracks={captionTextTracks}
                  />
                ) : (
                  <VideoPlayer
                    key="main-video-player-desktop-fullscreen"
                    url={resolvedMediaUrl}
                    thumbnailUrl={content?.content.thumbnail}
                    className="max-h-full max-w-full"
                    currentTime={currentTime}
                    onSeekTo={handleHtml5PlayerReady}
                    onTimeUpdate={onTimeUpdate}
                    onDurationChange={onDurationChange}
                    isPlaying={isPlaying}
                    onPlayPause={onPlayPause}
                    onEnded={onEnded}
                    title={primaryDisplayTitle}
                    artist={content?.content.author}
                    album="Podcast Episode"
                    muted={false}
                    enableTapToggle
                    maintainAspectRatio={false}
                    artwork={mediaArtwork}
                  />
                )
              )}

              <AnimatePresence mode="wait">
                {shouldShowDesktopFullscreenSubtitles && (
                  <motion.div
                    key={`desktop-fullscreen-subtitle-${
                      activeSubtitleText?.slice(0, 30) || "empty"
                    }-${
                      activeSubtitleTranslation?.slice(0, 30) || "empty"
                    }`}
                    initial={{ opacity: 0, y: 20, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 20, scale: 0.95 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="pointer-events-none absolute bottom-16 left-0 right-0 flex justify-center px-8"
                  >
                    <div className="bg-black/60 backdrop-blur-md border border-white/10 rounded-3xl shadow-lg px-6 py-4 text-white relative max-w-4xl">
                      <div className="relative z-10 text-center space-y-2">
                        {activeSubtitleText?.trim() && (
                          <div className="text-xl font-medium leading-tight drop-shadow-sm">
                            {renderSubtitleWithHoverCards(activeSubtitleText)}
                          </div>
                        )}
                        {shouldShowTranslation &&
                          activeSubtitleTranslation?.trim() && (
                            <div className="text-base text-purple-100/80 font-light leading-tight drop-shadow-sm">
                              {activeSubtitleTranslation}
                            </div>
                          )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <VideoDownloadDialog
          isOpen={showDownloadDialog}
          onClose={() => setShowDownloadDialog(false)}
          videoId={content?.content.videoId || ""}
          videoTitle={primaryDisplayTitle}
          onDownloadComplete={(downloadUrl) => {
            setLocalVideoUrl(downloadUrl);
            setShowDownloadDialog(false);
          }}
        />

        {isYouTubeVideo && !isYouTubeAccessible && !isChecking && !localVideoUrl && (
          <div className="fixed bottom-4 right-4 z-[100] max-w-sm">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 shadow-lg"
            >
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800">
                    YouTube not accessible
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Download the video to watch it locally
                  </p>
                  <Button
                    size="sm"
                    className="mt-2 h-7 bg-yellow-600 hover:bg-yellow-700 text-white"
                    onClick={() => setShowDownloadDialog(true)}
                  >
                    <Download className="h-3 w-3 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {/* Enhanced Maximize Button (visible only when minimized) */}
      {!isImmersiveMode && !mobileImmersiveMode && !fullscreenMode && isMinimized && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2 }}
          className="fixed top-[50px] sm:top-[46px] right-4 sm:right-8 z-50"
        >
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onToggleMinimize}
                  aria-label={minimizeButtonLabel}
                  className={`group h-9 w-9 p-0 rounded-xl transition-all duration-300 hover:scale-105 bg-primary/10 hover:bg-primary/20 border border-border/50 shadow-lg backdrop-blur-sm sm:text-inherit sm:bg-inherit sm:hover:bg-inherit sm:border-inherit sm:shadow-inherit sm:backdrop-blur-inherit
                    ${
                      typeof window !== "undefined" && window.innerWidth <= 640
                        ? "bg-transparent hover:bg-transparent border border-border/40 shadow-none"
                        : ""
                    }
                  `}
                >
                  <motion.div
                    key="maximize"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="relative"
                  >
                    <Maximize2 className="h-4 w-4 text-primary group-hover:scale-110 transition-transform" />
                  </motion.div>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left" className="px-2 py-1 text-xs">
                <p>{minimizeButtonLabel}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </motion.div>
      )}

      {/* {isImmersiveMode && (
        <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
        className="fixed top-[50px] sm:top-[46px] right-4 sm:right-8 z-50"
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={handleImmersiveModeToggle}
          className={`h-8 w-8 p-0 backdrop-blur-sm rounded-lg ${
            isMobile 
              ? 'bg-gray-600/50 text-gray-400 cursor-not-allowed' 
              : 'bg-black/50 hover:bg-black/70 text-white hover:text-white'
          }`}
          title={
            isMobile 
              ? 'Immersive mode not available on mobile' 
              : (isImmersiveMode ? 'Exit immersive mode' : 'Enter immersive mode')
          }
        >
          <Fullscreen className="h-4 w-4" />
        </Button>
        </motion.div>
      )} */}


      {/* Video Player Section Content - 保持元素始终存在 */}
      <div
        ref={fullscreenContainerRef}
        className={cn(
          "z-10 transition-all duration-300 ease-in-out",
          fullscreenMode
            ? isMobile
              ? "fixed inset-0 z-[60] w-screen h-screen bg-black/95 text-white pointer-events-auto"
              : "fixed inset-0 z-[60] w-screen h-screen bg-black text-white pointer-events-auto flex items-center justify-center"
            : mobileImmersiveMode
              ? "sticky top-0 sm:top-[40px] bg-background/95 backdrop-blur-sm"
              : cn(
                  "sticky top-0 sm:top-[40px] bg-background/95 backdrop-blur-sm border-border/50",
                  !isMinimized && "overflow-hidden max-h-[600px]"
                ),
          fullscreenMode && "px-0"
        )}
      >
        <div
          className={cn(
            "sm:p-0 p-2 pb-1 sm:pb-0",
            fullscreenMode &&
              (isMobile
                ? "h-full flex flex-col"
                : "w-full h-full flex flex-col items-center justify-center"),
            isMinimized && !fullscreenMode && !mobileImmersiveMode
              ? "hidden"
              : "block"
          )}
        >
          <div
            className={cn(
              "max-w-7xl mx-auto px-0",
              fullscreenMode &&
                (isMobile
                  ? "max-w-none w-full h-full"
                  : "max-w-full w-full h-full flex items-center justify-center")
            )}
          >
            <motion.div
              initial={false}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className={cn(
                "flex items-start space-y-0 p-0 sm:p-0 lg:p-2 transition-all duration-200 ease-in-out",
                isImmersiveMode
                  ? "flex-col lg:flex-col lg:space-x-0"
                  : "flex-col lg:flex-row lg:space-x-8 lg:space-y-0",
                fullscreenMode &&
                  (isMobile
                    ? "h-full items-stretch"
                    : "h-full justify-center items-center")
              )}
            >
              {/* Enhanced YouTube Player */}
              <motion.div
                initial={false}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.25 }}
                className={cn(
                  "relative group video-player-container transition-all duration-300 ease-in-out",
                  isImmersiveMode
                    ? "w-full min-h-[300px] sm:min-h-[400px] lg:min-h-[500px]"
                    : "w-full lg:w-[420px] lg:flex-shrink-0",
                  fullscreenMode
                    ? isMobile
                      ? "flex-1 h-full min-h-0"
                      : "w-full h-full flex items-center justify-center"
                    : "aspect-video"
                )}
                onTouchStart={() => showMobileControls(true)}
                onPointerDown={() => showMobileControls(true)}
              >
                <div
                  className={cn(
                    "relative overflow-hidden transition-all duration-300 rounded-none",
                    mobileImmersiveMode
                      ? ""
                      : "",
                    fullscreenMode &&
                      (isMobile
                        ? "rounded-none sm:rounded-none h-full w-full shadow-none ring-0 bg-black"
                        : "rounded-none sm:rounded-none h-full max-w-full shadow-none ring-0 bg-black flex items-center justify-center")
                  )}
                >
                  {/* Dynamic Player Selection based on content type and accessibility */}
                  {/* 🔋 优化：收起时隐藏视频并暂停播放，完全停止视频解码，减少 CPU/GPU 负载和发热 */}
                  <div className={isMinimized && !isPodcast ? "hidden" : "w-full h-full"}>
                    {isYouTubeVideo && !shouldUseVidstackForYouTube ? (
                      <YouTubePlayerV3
                        key="youtube-player-v3"
                        videoId={content.content.videoId}
                        className="w-full h-full"
                        currentTime={currentTime}
                        onReady={onYouTubeReady}
                        onTimeUpdate={onTimeUpdate}
                        onDurationChange={onDurationChange}
                        // 🔋 优化：收起时暂停视频播放，停止解码
                        isPlaying={isMinimized && !isPodcast ? false : isPlaying}
                        onPlayPause={onPlayPause}
                        onEnded={onEnded}
                        title={primaryDisplayTitle}
                        muted={false}
                      />
                    ) : (
                      USE_VIDSTACK_PLAYER ? (
                        <VideoPlayerVidstack
                          key="main-video-player"
                          url={resolvedMediaUrl}
                          thumbnailUrl={content?.content.thumbnail}
                          className={cn(
                            "h-full",
                            isDesktopFullscreen ? "max-w-full" : "w-full"
                          )}
                          currentTime={currentTime}
                          onSeekTo={handleHtml5PlayerReady}
                          onTimeUpdate={onTimeUpdate}
                          onDurationChange={onDurationChange}
                          // 🔋 优化：收起时暂停视频播放，停止解码
                          isPlaying={isMinimized && !isPodcast ? false : isPlaying}
                          onPlayPause={onPlayPause}
                          onEnded={onEnded}
                          title={primaryDisplayTitle}
                          artist={content?.content.author}
                          album="Podcast Episode"
                          muted={false}
                          enableTapToggle={fullscreenMode}
                          maintainAspectRatio={!isDesktopFullscreen}
                          artwork={mediaArtwork}
                          immersiveSlot={immersiveLayoutSlot}
                          textTracks={captionTextTracks}
                        />
                      ) : (
                        <VideoPlayer
                          key="main-video-player"
                          url={resolvedMediaUrl}
                          thumbnailUrl={content?.content.thumbnail}
                          className={cn(
                            "h-full",
                            isDesktopFullscreen ? "max-w-full" : "w-full"
                          )}
                          currentTime={currentTime}
                          onSeekTo={handleHtml5PlayerReady}
                          onTimeUpdate={onTimeUpdate}
                          onDurationChange={onDurationChange}
                          isPlaying={isPlaying}
                          onPlayPause={onPlayPause}
                          onEnded={onEnded}
                          title={primaryDisplayTitle}
                          artist={content?.content.author}
                          album="Podcast Episode"
                          muted={false}
                          enableTapToggle={fullscreenMode}
                          maintainAspectRatio={!isDesktopFullscreen}
                          artwork={mediaArtwork}
                        />
                      )
                    )}
                  </div>
                  {/* Play/Pause Overlay Indicator */}
                  {/* <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <div className="bg-black/50 backdrop-blur-sm rounded-full p-2">
                      {isPlaying ? (
                        <Pause className="h-4 w-4 text-white" />
                      ) : (
                        <Play className="h-4 w-4 text-white ml-0.5" />
                      )}
                    </div>
                  </div> */}

                  {/* Logo - Mobile Immersive Mode Only */}
                  {/* {isMobile && mobileImmersiveMode && (
                    <div className="absolute top-3 left-3 z-20">
                      <div className="flex items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 500 500"
                          className="h-8 w-8 fill-white drop-shadow-md"
                        >
                          <g transform="translate(0.000000,500.000000) scale(0.100000,-0.100000)">
                            <path d="M1420 4950 c-153 -21 -252 -50 -375 -112 -107 -54 -196 -121 -291 -220 -152 -160 -230 -307 -281 -534 -17 -73 -18 -160 -18 -1214 l0 -1135 26 -101 c15 -55 38 -129 52 -162 130 -322 441 -588 777 -666 36 -8 79 -18 95 -22 17 -4 237 -10 490 -13 l460 -6 33 -23 c61 -44 65 -62 72 -327 7 -249 12 -278 53 -324 28 -30 94 -61 132 -61 47 0 94 27 350 200 132 89 318 214 413 278 95 64 184 124 197 132 13 8 84 56 157 105 400 271 574 442 684 670 56 116 91 238 104 366 14 142 14 2104 -1 2224 -13 108 -66 275 -115 365 -131 238 -369 441 -619 527 -167 58 -130 56 -1280 58 -583 2 -1085 -1 -1115 -5z m894 -816 c14 -6 16 -102 16 -990 l0 -984 543 -2 542 -3 3 -271 2 -272 -22 -6 c-42 -11 -1638 -7 -1659 4 -19 11 -19 35 -19 1264 0 689 3 1256 7 1259 8 9 566 9 587 1z" />
                          </g>
                        </svg>
                        <span className="text-white text-sm font-medium drop-shadow-md">
                          lingoflows.com
                        </span>
                      </div>
                    </div>
                  )} */}

                  {/* Immersive Mode Toggle - Hide when logo is showing */}
                  {showImmersiveOverlay && (
                    <div className={immersiveButtonWrapperClasses}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleImmersiveModeToggle}
                        className={immersiveButtonClasses}
                        title={immersiveButtonTitle}
                        aria-label={immersiveButtonTitle}
                        aria-pressed={immersiveModeActive}
                      >
                        <ImmersiveToggleIcon className="h-4 w-4" />
                        {!isMobile && (
                          <>
                            <span>{immersiveButtonLabel}</span>
                            {showEscapeHint && (
                              <span className="rounded-full border border-white/30 bg-white/10 px-2 py-[1px] text-[10px] font-semibold uppercase tracking-[0.12em] text-white/80">
                                Esc
                              </span>
                            )}
                          </>
                        )}
                      </Button>
                      {isMobile && fullscreenMode && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onPointerDown={handleMobileSubtitlesPointerDown}
                          onClick={handleMobileSubtitlesClick}
                          aria-live="polite"
                          className={mobileSubtitlesButtonClasses}
                          title={toggleSubtitlesLabel}
                          aria-label={toggleSubtitlesLabel}
                          aria-pressed={subtitlesVisible}
                        >
                          <Subtitles className="h-4 w-4" />
                          <span className="sr-only">
                            {subtitlesVisible
                              ? t('episode.videoPlayer.immersive.subtitlesOn', 'Subtitles on')
                              : t('episode.videoPlayer.immersive.subtitlesOff', 'Subtitles off')}
                          </span>
                        </Button>
                      )}
                    </div>
                  )}

                  {/* 🚀 性能优化：字幕渲染优化 - 仅在桌面沉浸模式显示 */}
                  <AnimatePresence mode="wait">
                    {((isImmersiveMode && !mobileImmersiveMode) || fullscreenMode) &&
                      subtitlesVisible &&
                      (activeSubtitleText?.trim() ||
                        activeSubtitleTranslation?.trim()) && (
                        <motion.div
                          key={`subtitle-${
                            activeSubtitleText?.slice(0, 30) || "empty"
                          }-${
                            activeSubtitleTranslation?.slice(0, 30) || "empty"
                          }`}
                          initial={{ opacity: 0, y: 20, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -20, scale: 0.9 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                          className={cn(
                            "absolute left-0 right-0 z-20 flex justify-center px-4 bottom-6",
                            fullscreenMode && "pointer-events-none select-none"
                          )}
                          style={
                            fullscreenMode
                              ? { bottom: "calc(env(safe-area-inset-bottom, 12px) + 1.25rem)" }
                              : undefined
                          }
                        >
                          <div className="bg-black/50 backdrop-blur-md border border-white/10 rounded-3xl shadow-lg px-4 py-3 text-white relative max-w-[88%] sm:max-w-[70%]">
                            <div className="relative z-10 text-center space-y-1">
                              {/* English Text with highlighted vocabulary terms */}
                              {activeSubtitleText?.trim() && (
                                <div className="text-base sm:text-xl font-medium text-white drop-shadow-sm leading-tight">
                                  {renderSubtitleWithHoverCards(activeSubtitleText)}
                                </div>
                              )}

                              {/* Chinese Translation */}
                              {shouldShowTranslation &&
                                activeSubtitleTranslation?.trim() && (
                                <div className="text-sm sm:text-sm text-purple-100/70 font-light leading-tight drop-shadow-sm mt-1">
                                  {activeSubtitleTranslation}
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                  </AnimatePresence>

                  {shouldShowMobileFullscreenControls && (
                    <div
                      className="absolute inset-x-0 px-6"
                      style={{
                        bottom: `calc(env(safe-area-inset-bottom, 12px) + 28px)`,
                        zIndex: 35,
                        pointerEvents: "auto",
                      }}
                    >
                      <div className="flex flex-col gap-2 rounded-2xl bg-black/40 px-3 py-3 backdrop-blur-md">
                        <div className="flex items-center justify-between text-xs text-white/80">
                          <span>{formattedCurrentTime}</span>
                          <span>{formattedTotalTime}</span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={sliderMax}
                          step={0.1}
                          value={Math.max(0, Math.min(sliderValue, sliderMax))}
                          onChange={handleSliderChange}
                          onPointerDown={handleSliderPointerDown}
                          onPointerUp={handleSliderPointerUp}
                          onPointerCancel={handleSliderPointerUp}
                          className="h-1 w-full accent-white"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* 移动端沉浸模式：字幕显示现在移动到MobileImmersiveContent组件中 */}
              {false && mobileImmersiveMode && isMobile && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 px-12 pb-2 flex flex-col items-center"
                >
                  {/* 手势提示 - 仅在进入沉浸模式的前几秒显示 */}
                  <motion.div
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 0 }}
                    transition={{ delay: 3, duration: 1 }}
                    className="text-center mb-4"
                  >
                    <div className="inline-flex items-center gap-2 bg-black/20 backdrop-blur-sm rounded-full px-3 py-1.5 text-xs text-white/80">
                      <motion.div
                        animate={{ y: [0, -2, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        👆
                      </motion.div>
                      <span>Tap video to exit focus mode</span>
                    </div>
                  </motion.div>
                  <AnimatePresence mode="wait">
                    {subtitlesVisible &&
                      (activeSubtitleText?.trim() ||
                        activeSubtitleTranslation?.trim()) && (
                        <motion.div
                          key={`mobile-subtitle-${
                            activeSubtitleText?.slice(0, 30) || "empty"
                          }-${
                            activeSubtitleTranslation?.slice(0, 30) || "empty"
                          }`}
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.95 }}
                          transition={{
                            duration: 0.25,
                            ease: "easeOut",
                            scale: { duration: 0.2 },
                          }}
                          className="flex justify-center w-full"
                        >
                          {/* 优化的字幕容器 - 现代化设计 */}
                          <div className="bg-gradient-to-br from-background/95 via-background/90 to-background/95 backdrop-blur-md border border-border/30 rounded-2xl px-4 py-3 text-center space-y-2 shadow-lg shadow-black/5 relative overflow-hidden min-w-[280px] max-w-md mx-auto">
                            {/* 装饰性渐变背景 */}
                            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5 opacity-50"></div>

                            {/* 内容区域 */}
                            <div className="relative z-10 space-y-2">
                              {/* English Text with enhanced styling */}
                              {activeSubtitleText?.trim() && (
                                <div className="text-base font-semibold text-foreground leading-snug tracking-wide text-center">
                                  {renderSubtitleWithHoverCards(
                                    activeSubtitleText!
                                  )}
                                </div>
                              )}

                              {/* 分隔线 */}
                              {activeSubtitleText?.trim() &&
                                shouldShowTranslation &&
                                activeSubtitleTranslation?.trim() && (
                                  <div className="flex items-center justify-center py-0.5">
                                    <div className="h-px bg-gradient-to-r from-transparent via-border/50 to-transparent w-12"></div>
                                  </div>
                                )}

                              {/* Chinese Translation with enhanced styling */}
                              {shouldShowTranslation &&
                                activeSubtitleTranslation?.trim() && (
                                <div className="text-sm text-muted-foreground/90 leading-snug font-normal text-center">
                                  {activeSubtitleTranslation}
                                </div>
                              )}
                            </div>

                            {/* 底部装饰条 */}
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20"></div>
                          </div>
                        </motion.div>
                      )}
                  </AnimatePresence>
                </motion.div>
              )}

              {/* Enhanced Episode Info - Hidden on mobile and in immersive mode */}
              <motion.div
                initial={{ x: 20, opacity: 0 }}
                animate={{
                  x: 0,
                  opacity: isImmersiveMode || mobileImmersiveMode || fullscreenMode ? 0 : 1,
                }}
                transition={{ delay: 0.2, duration: 0.25 }}
                className={`flex-1 flex-col space-y-4 transition-all duration-300 ease-in-out ${
                  isImmersiveMode ? "hidden" : "hidden lg:flex"
                }`}
              >
                {/* Metadata with icons */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {content?.content.publishDate &&
                        new Date(
                          content.content.publishDate
                        ).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-4 w-4" />
                    <span>{formatDuration(youtubeDuration)}</span>
                  </div>
                </div>

                {/* Episode Title */}
                <div className="space-y-3">
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold leading-tight text-foreground line-clamp-2">
                    {primaryDisplayTitle}
                  </h2>
                  {secondaryDisplayTitle && (
                    <p className="text-sm sm:text-base text-muted-foreground">
                      {secondaryDisplayTitle}
                    </p>
                  )}

                  {/* Episode Description (if available) */}
                  {content?.content.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                      {description}
                    </p>
                  )}
                </div>

                {/* Channel/Author Info */}
                {/* 点击作者跳转到频道页，例如 BBC World Service -> /channels/BBC_World_Service */}
                <a
                  href={
                    content?.content.author
                      ? `/channels/${content.content.author.replace(
                          /\s+/g,
                          "_"
                        )}`
                      : "#"
                  }
                  className="inline-flex items-center gap-3 p-3 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors group border border-border/50 cursor-pointer"
                  tabIndex={0}
                >
                  <div className="relative">
                    <div className="w-10 h-10 rounded-xl overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                      {(content?.ownerAvatarUrl || content?.content?.ownerAvatarUrl) ? (
                        <img
                          src={decodeURIComponent(content?.ownerAvatarUrl || content?.content?.ownerAvatarUrl || '')}
                          alt={content.content.author}
                          width={40}
                          height={40}
                          className="rounded-xl w-full h-full object-cover"
                          onError={(e) => {
                            console.error('Avatar image failed to load:', e.currentTarget.src);
                            // Hide the broken image and show fallback
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                              parent.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl flex items-center justify-center"><svg class="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg></div>';
                            }
                          }}
                        />
                      ) : (
                        <User className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary/20 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground group-hover:text-primary transition-colors">
                      {content?.content.author}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Podcast Host
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </a>
              </motion.div>

              {/* Mobile-only minimal episode info - Hidden in immersive mode */}
              {/* <motion.div
                initial={{ opacity: 0 }}
                animate={{
                  opacity: isImmersiveMode || mobileImmersiveMode || fullscreenMode ? 0 : 1,
                }}
                transition={{ delay: 0.2, duration: 0.2 }}
                className={`w-full transition-opacity duration-200 ease-in-out ${
                  isImmersiveMode || mobileImmersiveMode || fullscreenMode
                    ? "hidden"
                    : "lg:hidden"
                }`}
              >
                <div className="flex items-center justify-between px-2 py-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>{formatDuration(youtubeDuration)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onStarClick}
                      className="h-7 w-7 p-0 rounded-lg"
                      title={
                        isStarred ? "Remove from favorites" : "Add to favorites"
                      }
                    >
                      <Star
                        className={`h-3 w-3 ${
                          isStarred
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-muted-foreground"
                        }`}
                      />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onShareClick}
                      className="h-7 w-7 p-0 rounded-lg"
                      title="Share Episode"
                    >
                      <Send className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </motion.div> */}
            </motion.div>

          </div>
        </div>

        {!mobileImmersiveMode && !fullscreenMode && (
          <div className="max-w-7xl mx-auto px-0 py-0 md:py-0 sm:py-3">
            <ContentTabs
              activeTab={contentTabsActiveTab}
              onTabChange={onContentTabChange}
              isImmersiveMode={isImmersiveMode}
              onShareClick={onShareClick}
              onStarClick={onStarClick}
              onAddToListClick={onAddToListClick}
              isStarred={isStarred}
            />
          </div>
        )}
      </div>

      {isMobile && selectedWord && (
        <Drawer
          open={showWordDrawer}
          onOpenChange={(open) => {
            setShowWordDrawer(open);
            if (!open) {
              setSelectedWord("");
            }
          }}
        >
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle className="flex items-center space-x-2">
                <GraduationCap className="h-6 w-6" />
                <span className="text-2xl font-bold">{selectedWord}</span>
              </DrawerTitle>
              {(translations[selectedWord]?.usphone ||
                translations[selectedWord]?.phonetic ||
                explanations[selectedWord]?.usphone) && (
                <DrawerDescription className="text-base text-start pl-8 font-medium text-stone-700">
                  /
                  {translations[selectedWord]?.usphone ||
                    translations[selectedWord]?.phonetic ||
                    explanations[selectedWord]?.usphone}
                  /
                </DrawerDescription>
              )}
            </DrawerHeader>

            <div className="px-4 pb-4">
              {youdaoLoading[selectedWord] || explanationLoading[selectedWord] ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-2 text-gray-600">Loading...</span>
                </div>
              ) : translations[selectedWord] || explanations[selectedWord] ? (
                <div className="space-y-4">
                  {(translations[selectedWord]?.translation ||
                    translations[selectedWord]?.explains?.[0] ||
                    explanations[selectedWord]?.translation) && (
                    <div className="flex items-start space-x-3">
                      <MessageCircle className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                      <div className="text-md font-medium text-primary">
                        {translations[selectedWord] ? (
                          <div>
                            {translations[selectedWord]?.translation ||
                              translations[selectedWord]?.explains?.[0]}
                          </div>
                        ) : (
                          explanations[selectedWord]?.translation
                            ?.split(';')
                            .map((part, idx) => (
                              <div key={idx} className={idx > 0 ? "mt-2" : ""}>
                                {part.trim()}
                              </div>
                            ))
                        )}
                      </div>
                    </div>
                  )}

                  {selectedWord &&
                    translations[selectedWord]?.explains &&
                    (translations[selectedWord]?.explains?.length || 0) > 1 && (
                      <div className="flex items-start space-x-3 pt-3 border-t border-gray-100">
                        <Quote className="h-5 w-5 text-gray-600 mt-1 flex-shrink-0" />
                        <div className="text-sm text-gray-600">
                          {translations[selectedWord]?.explains
                            ?.slice(1)
                            .map((explain, idx) => (
                              <div key={idx} className={idx > 0 ? "mt-1" : ""}>
                                {explain}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  {selectedWord &&
                    !translations[selectedWord] &&
                    explanations[selectedWord]?.example && (
                      <div className="flex items-start space-x-3 pt-3 border-t border-gray-100">
                        <Quote className="h-5 w-5 text-gray-600 mt-1 flex-shrink-0" />
                        <span className="text-base text-gray-600 italic">
                          {explanations[selectedWord]?.example}
                        </span>
                      </div>
                    )}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  No explanation available
                </div>
              )}
            </div>

            <DrawerFooter>
              <div className="flex space-x-2">
                <Button
                  variant="ghost"
                  size="lg"
                  className="flex-1 border rounded-lg"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (speakerRef.current) {
                      speakerRef.current.click();
                    }
                  }}
                  disabled={!selectedWord}
                >
                  <SpeakerIcon
                    divRef={speakerRef}
                    size={10}
                    provider="EdgeTTS"
                    text={selectedWord}
                    lang={getSpeakerLang(
                      userSettings?.languageSetting || "english"
                    ) as any}
                    voice={undefined}
                    rate={10}
                    volume={100}
                  />
                  <span className="ml-2">Speak</span>
                </Button>
              </div>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}

      {/* YouTube Download Dialog */}
      <VideoDownloadDialog
        isOpen={showDownloadDialog}
        onClose={() => setShowDownloadDialog(false)}
        videoId={content?.content.videoId || ''}
        videoTitle={primaryDisplayTitle}
        onDownloadComplete={(downloadUrl) => {
          setLocalVideoUrl(downloadUrl);
          setShowDownloadDialog(false);
        }}
      />

      {/* YouTube Accessibility Warning */}
      {isYouTubeVideo && !isYouTubeAccessible && !isChecking && !localVideoUrl && (
        <div className="fixed bottom-4 right-4 z-[100] max-w-sm">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 shadow-lg"
          >
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800">
                  YouTube not accessible
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Download the video to watch it locally
                </p>
                <Button
                  size="sm"
                  className="mt-2 h-7 bg-yellow-600 hover:bg-yellow-700 text-white"
                  onClick={() => setShowDownloadDialog(true)}
                >
                  <Download className="h-3 w-3 mr-1" />
                  Download
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}

// 🚀 性能优化：使用 React.memo 避免不必要的重渲染
export const VideoPlayerSection = React.memo(
  VideoPlayerSectionComponent,
  (prevProps, nextProps) => {
    // 🔥 content 相关属性比较（修复 ownerAvatarUrl 不更新的问题）
    const prevContent = prevProps.content;
    const nextContent = nextProps.content;
    if (prevContent !== nextContent) {
      // 检查关键字段是否变化
      const prevOwnerAvatar = prevContent?.ownerAvatarUrl || prevContent?.content?.ownerAvatarUrl;
      const nextOwnerAvatar = nextContent?.ownerAvatarUrl || nextContent?.content?.ownerAvatarUrl;
      if (prevOwnerAvatar !== nextOwnerAvatar) return false;
      
      const prevTitle = prevContent?.content?.title;
      const nextTitle = nextContent?.content?.title;
      if (prevTitle !== nextTitle) return false;
      
      const prevVideoUrl = prevContent?.content?.videoUrl;
      const nextVideoUrl = nextContent?.content?.videoUrl;
      if (prevVideoUrl !== nextVideoUrl) return false;
    }

    // 沉浸模式相关的关键属性比较
    if (prevProps.isImmersiveMode !== nextProps.isImmersiveMode) return false;
    if (prevProps.activeSubtitleText !== nextProps.activeSubtitleText)
      return false;
    if (
      prevProps.activeSubtitleTranslation !==
      nextProps.activeSubtitleTranslation
    )
      return false;
    if (
      prevProps.showTranslationInImmersive !==
      nextProps.showTranslationInImmersive
    )
      return false;
    if (
      prevProps.showSubtitlesInImmersive !==
      nextProps.showSubtitlesInImmersive
    )
      return false;

    // 词汇高亮相关的属性比较
    if (
      JSON.stringify(prevProps.activeVocabularyTerms) !==
      JSON.stringify(nextProps.activeVocabularyTerms)
    )
      return false;
    if (prevProps.contentTabsActiveTab !== nextProps.contentTabsActiveTab)
      return false;
    if (prevProps.onContentTabChange !== nextProps.onContentTabChange)
      return false;

    // 其他关键属性比较
    if (prevProps.isPlaying !== nextProps.isPlaying) return false;
    if (prevProps.isMinimized !== nextProps.isMinimized) return false;
    if (prevProps.isStarred !== nextProps.isStarred) return false;
    if (prevProps.isLoading !== nextProps.isLoading) return false;

    return true;
  }
);
